//==================================================================================================
// Shipment events table (Movements-like).
//
// Responsibilities:
// - Pure rendering component: receives a provider-agnostic "events" array.
// - Client-side pagination (PrimeReact Paginator).
// - Automatic layout selection based on payload shape:
//     - Ocean-like events: show Vessel column.
//     - Air-like events: show Flight and Pieces columns when available.
//
// Rendering goals:
// - Keep a stable, readable table even when event fields differ between providers/modes.
// - Avoid hard-coding the table to a single transport mode.
//==================================================================================================

import React, { useMemo, useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Paginator } from "primereact/paginator";

import { headerCellStyle, cellStyle as tdStyle } from "src/components/tableHelpers/TableStyleUtils";

import "./TrackingEventsTable.css";

export default function TrackingEventsTable({ events }) {
    const { t } = useTranslation();

    // #region Pagination state (client-side)

    const [first, setFirst] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const onPageChange = useCallback((e) => {
        setFirst(e.first);
        setRowsPerPage(e.rows);
    }, []);


    /**
     * Normalizes and sorts events by timestamp (most recent first).
     * Sorting uses Timestamp when present; missing/invalid timestamps are treated as epoch (oldest).
     */
    const sorted = useMemo(() => {
        const arr = Array.isArray(events) ? [...events] : [];

        arr.sort((a, b) => {
            const ta = safeTime(a?.Timestamp);
            const tb = safeTime(b?.Timestamp);
            return tb - ta;
        });

        return arr;
    }, [events]);

    /**
     * Current page slice for rendering.
     */
    const page = useMemo(() => sorted.slice(first, first + rowsPerPage), [sorted, first, rowsPerPage]);

    /**
     * Resets pagination when dataset changes, ensuring page index is always valid.
     */
    useEffect(() => {
        setFirst(0);
    }, [sorted.length]);

    // #endregion

    // #region Layout inference (Ocean vs Air)

    /**
     * Detects whether any event includes vessel-related metadata.
     * When present, the dataset is treated as Ocean-like for table layout purposes.
     */
    const hasOceanVesselInfo = useMemo(() => {
        return sorted.some((ev) => {
            const vesselName = getMeta(ev, "vessel_name");
            const vesselImo = getMeta(ev, "vessel_imo");
            return hasValue(vesselName) || hasValue(vesselImo);
        });
    }, [sorted]);

    /**
     * Detects whether any event includes air-related metadata.
     * When present, the dataset is treated as Air-like for table layout purposes.
     */
    const hasAirInfo = useMemo(() => {
        return sorted.some((ev) => {
            const airEvent = getMeta(ev, "air_event");
            const airStatus = getMeta(ev, "air_status");
            const flight = getMeta(ev, "air_flight");
            return hasValue(airEvent) || hasValue(airStatus) || hasValue(flight);
        });
    }, [sorted]);

    /**
     * Determines whether Pieces should be displayed for Air-like datasets.
     * The column is shown only when at least one event includes cargo_pieces.
     */
    const hasAirPieces = useMemo(() => {
        return sorted.some((ev) => {
            const pieces = getMeta(ev, "cargo_pieces");
            return Number.isFinite(Number(pieces)) && Number(pieces) > 0;
        });
    }, [sorted]);

    /**
     * Effective layout mode for the table.
     * Vessel presence takes precedence because Ocean events can also include generic fields.
     */
    const layout = useMemo(() => {
        if (hasOceanVesselInfo) return "ocean";
        if (hasAirInfo) return "air";
        return "generic";
    }, [hasOceanVesselInfo, hasAirInfo]);

    /**
     * Builds the column set based on the inferred layout.
     * Columns are defined as render functions to handle heterogeneous event shapes safely.
     */
    const columns = useMemo(() => {
        /**
         * Location is stable across both payloads via event.Location.
         */
        const base = [
            {
                key: "location",
                header: t("Location") || "Location",
                className: "col-location",
                render: (ev) => fmtLocation(ev?.Location),
            },
            {
                key: "move",
                header: t("Move") || "Move",
                className: "col-move",
                render: (ev) => fmtMove(ev),
            },
            {
                key: "date",
                header: t("Date") || "Date",
                className: "col-date",
                render: (ev) => fmtDate(ev?.Timestamp),
            },
        ];

        /**
         * Ocean-like layout:
         * - Vessel is the primary extra column displayed in provider UIs.
         */
        if (layout === "ocean") {
            return [
                ...base,
                {
                    key: "vessel",
                    header: t("Vessel") || "Vessel",
                    className: "col-vessel",
                    render: (ev) => fmtVessel(ev),
                },
            ];
        }

        /**
         * Air-like layout:
         * - Flight is displayed when present (or "-" when absent).
         * - Pieces is displayed only when cargo_pieces exists in at least one event.
         */
        if (layout === "air") {
            const airCols = [
                ...base,
                {
                    key: "flight",
                    header: t("Flight") || "Flight",
                    className: "col-flight",
                    render: (ev) => fmtFlight(ev),
                },
            ];

            if (hasAirPieces) {
                airCols.unshift({
                    key: "pieces",
                    header: t("Pieces") || "Pieces",
                    className: "col-pieces",
                    render: (ev) => fmtPieces(ev),
                });
            }

            return airCols;
        }

        /**
         * Generic layout:
         * - Adds a conservative "Details" column when no mode-specific metadata is detected.
         * - Uses Description as a stable fallback signal.
         */
        return [
            ...base,
            {
                key: "details",
                header: t("Details") || "Details",
                className: "col-details",
                render: (ev) => fmtDetails(ev),
            },
        ];
    }, [layout, hasAirPieces, t]);

    /**
     * Stable row key builder for heterogeneous datasets.
     * Uses timestamp + event code hints + index fallback.
     */
    const buildRowKey = useCallback((ev, idx) => {
        const ts = String(ev?.Timestamp ?? "");
        const oceanCode = String(getMeta(ev, "event_code") ?? "");
        const airCode = String(getMeta(ev, "air_event") ?? "");
        const hint = oceanCode || airCode || String(ev?.Description ?? "");
        return `${ts}-${hint}-${idx}`;
    }, []);

    return (
        <div className="trackingEvents-wrapper">
            <div className="trackingEvents-table-container">
                <table className="trackingEvents">
                    <thead>
                        <tr>
                            {columns.map((c) => (
                                <th
                                    key={c.key}
                                    style={headerCellStyle()}
                                    className={`trackingEvents-header-cell ${c.className ?? ""}`}
                                >
                                    {c.header}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {page.length === 0 ? (
                            <tr>
                                <td className="trackingEvents-cell" style={tdStyle()} colSpan={columns.length}>
                                    {t("NoData") || "No data"}
                                </td>
                            </tr>
                        ) : (
                            page.map((ev, idx) => (
                                <tr key={buildRowKey(ev, idx)}>
                                    {columns.map((c) => (
                                        <td
                                            key={c.key}
                                            className={`trackingEvents-cell ${c.className ?? ""}`}
                                            style={tdStyle()}
                                        >
                                            {c.render(ev)}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <Paginator
                first={first}
                rows={rowsPerPage}
                totalRecords={sorted.length}
                rowsPerPageOptions={[10, 20, 50]}
                onPageChange={onPageChange}
                template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            />
        </div>
    );


}


TrackingEventsTable.propTypes = {
    /**
     * Events array to render.
     * Expected shape is the provider-agnostic event model used by the backend mapping.
     */
    events: PropTypes.array,
};


//==================================================================================================
// Private helpers
//==================================================================================================


/**
 * Converts a timestamp value into a numeric time for sorting.
 *
 * @param {string|Date|number|null|undefined} value Timestamp value.
 * @returns {number} Milliseconds since epoch or 0 on invalid input.
 */
function safeTime(value) {
    if (!value) return 0;
    const d = new Date(value);
    const t = d.getTime();
    return Number.isNaN(t) ? 0 : t;
}

/**
 * Formats an ISO timestamp into a stable string (no locale assumptions).
 *
 * @param {string} iso ISO datetime string.
 * @returns {string} Safe formatted string.
 */
function fmtDate(iso) {
    if (!iso) return "-";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return String(iso);
        return d.toISOString().replace("T", " ").replace("Z", "");
    } catch {
        return String(iso);
    }
}

/**
 * Returns a metadata value using both casing variants commonly found in responses:
 * - Metadata (PascalCase)
 * - metadata (camelCase)
 *
 * @param {object} ev Tracking event.
 * @param {string} key Metadata key.
 * @returns {any} Metadata value or undefined.
 */
function getMeta(ev, key) {
    if (!ev) return undefined;
    const a = ev?.Metadata?.[key];
    if (a !== undefined) return a;
    return ev?.metadata?.[key];
}

/**
 * Checks if a value is present and non-empty when treated as a string.
 *
 * @param {any} v Value to test.
 * @returns {boolean} True when value contains a non-whitespace representation.
 */
function hasValue(v) {
    if (v == null) return false;
    return String(v).trim().length > 0;
}


/**
 * Builds a safe location label.
 *
 * @param {object} loc Location object: { Name, Code, Country }
 * @returns {string} Label.
 */
function fmtLocation(loc) {
    if (!loc) return "-";
    const name = String(loc?.Name ?? "");
    const code = String(loc?.Code ?? "");
    const country = String(loc?.Country ?? "");

    if (name && code) return `${name} (${code})`;
    if (name) return name;
    if (code) return code;
    if (country) return country;
    return "-";
}

/**
 * Builds the "Move" cell, prioritizing the backend-provided Description.
 * Air events often embed the flight in Description already; the formatter keeps the original text.
 *
 * @param {object} ev Tracking event.
 * @returns {string} Move label.
 */
function fmtMove(ev) {
    const desc = ev?.Description;
    if (hasValue(desc)) return String(desc);
    return "-";
}

/**
 * Returns a vessel label for an event using vessel metadata when present.
 *
 * @param {object} ev Tracking event.
 * @returns {string} Vessel label or "-".
 */
function fmtVessel(ev) {
    const name = getMeta(ev, "vessel_name");
    const imo = getMeta(ev, "vessel_imo");

    if (hasValue(name) && hasValue(imo)) return `${String(name)} (${String(imo)})`;
    if (hasValue(name)) return String(name);
    if (hasValue(imo)) return String(imo);

    return "-";
}

/**
 * Returns a flight label for an event using air metadata when present.
 *
 * @param {object} ev Tracking event.
 * @returns {string} Flight label or "-".
 */
function fmtFlight(ev) {
    const flight = getMeta(ev, "air_flight");
    if (hasValue(flight)) return String(flight);
    return "-";
}

/**
 * Returns the pieces count for an event using cargo metadata when present.
 *
 * @param {object} ev Tracking event.
 * @returns {string} Pieces count or "-".
 */
function fmtPieces(ev) {
    const pieces = getMeta(ev, "cargo_pieces");
    const n = Number(pieces);
    if (Number.isFinite(n) && n > 0) return String(n);
    return "-";
}

/**
 * Returns a generic details cell for datasets that do not expose mode-specific metadata.
 *
 * @param {object} ev Tracking event.
 * @returns {string} Details label.
 */
function fmtDetails(ev) {
    const desc = ev?.Description;
    if (hasValue(desc)) return String(desc);
    return "-";
}
